import {
  useGetParentFeedbackSessions,
  useGetParentViewCompletedCourses,
  useGetParentViewCoursesInProgress,
  useGetParentViewUpcomingCourses,
} from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { FeedbackSection } from '@modules/SessionDetails/SessionDetailsFeedback';
import SessionDetailsFeedbackLabelValue from '@modules/SessionDetails/components/SessionDetailsFeedbackLabelValue';
import { useTranslation } from 'apps/agora/src/TranslationContext';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

type CourseStatus = 'in-progress' | 'completed' | 'upcoming';

interface FeedbackCourse {
  id: string;
  title: string;
  status: CourseStatus;
  enrollmentId?: string;
  duplicateIndex?: number;
}

const ParentPageFeedbackSection = () => {
  const { t, language } = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();
  const history = useHistory();
  const token = localStorage.getItem('parentToken') ?? '';

  const { data: inProgress, isLoading: loadingInProgress } = useGetParentViewCoursesInProgress({
    userId: studentId,
    token,
  });
  const { data: completed, isLoading: loadingCompleted } = useGetParentViewCompletedCourses({
    userId: studentId,
    token,
  });
  const { data: upcoming, isLoading: loadingUpcoming } = useGetParentViewUpcomingCourses({
    userId: studentId,
    token,
  });

  const statusLabel = (status: CourseStatus) => {
    if (status === 'in-progress') return t('parent_feedback_in_progress');
    if (status === 'completed') return t('parent_feedback_completed');
    return t('parent_feedback_upcoming');
  };

  const courses: FeedbackCourse[] = useMemo(() => {
    const list: FeedbackCourse[] = [];
    (inProgress?.courses ?? []).forEach((course) =>
      list.push({
        id: course.enrollmentId,
        title: course.title,
        status: 'in-progress',
        enrollmentId: course.enrollmentId,
      })
    );
    (completed?.courses ?? []).forEach((course) =>
      list.push({
        id: course.enrollmentId,
        title: course.title,
        status: 'completed',
        enrollmentId: course.enrollmentId,
      })
    );
    (upcoming?.courses ?? []).forEach((course, index) =>
      list.push({ id: `upcoming-${course._id}-${index}`, title: course.title, status: 'upcoming' })
    );

    const titleTotals: Record<string, number> = {};
    list.forEach((course) => (titleTotals[course.title] = (titleTotals[course.title] ?? 0) + 1));
    const seen: Record<string, number> = {};
    return list.map((course) => {
      if (titleTotals[course.title] <= 1) return course;
      seen[course.title] = (seen[course.title] ?? 0) + 1;
      return { ...course, duplicateIndex: seen[course.title] };
    });
  }, [inProgress, completed, upcoming]);

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [sessionIndex, setSessionIndex] = useState(0);

  useEffect(() => {
    if (selectedId || !courses.length) return;
    setSelectedId(courses[0].id);
  }, [courses, selectedId]);

  useEffect(() => {
    setSessionIndex(0);
  }, [selectedId]);

  const selectedCourse = courses.find((course) => course.id === selectedId);

  const { data: feedbackData, isLoading: loadingSessions } = useGetParentFeedbackSessions(
    selectedCourse?.enrollmentId,
    { userId: studentId, token }
  );

  const feedbackSessions = feedbackData?.sessions ?? [];
  const currentSession = feedbackSessions[sessionIndex];
  const feedback = currentSession?.mentorFeedback;

  const feedbackSections: FeedbackSection[] = (
    feedback
      ? [
          {
            label: t('sessionDetails.mentorFeedback.sessionSummary'),
            value:
              language === 'ro'
                ? feedback.sessionSummaryRo || feedback.sessionSummary
                : feedback.sessionSummary,
          },
          {
            label: t('sessionDetails.mentorFeedback.nextTopics'),
            value:
              language === 'ro'
                ? feedback.nextTopicsRo || feedback.nextTopics
                : feedback.nextTopics,
          },
          {
            label: t('sessionDetails.mentorFeedback.homework'),
            value:
              language === 'ro'
                ? feedback.homework?.explanationRo || feedback.homework?.explanation
                : feedback.homework?.explanation,
            starRating: feedback.homework?.rate,
          },
          {
            label: t('sessionDetails.mentorFeedback.engagement'),
            value:
              language === 'ro'
                ? feedback.engagement?.explanationRo || feedback.engagement?.explanation
                : feedback.engagement?.explanation,
            starRating: feedback.engagement?.rate,
          },
          {
            label: t('sessionDetails.mentorFeedback.progress'),
            value:
              language === 'ro'
                ? feedback.progress?.explanationRo || feedback.progress?.explanation
                : feedback.progress?.explanation,
            starRating: feedback.progress?.rate,
          },
          {
            label: t('sessionDetails.mentorFeedback.parentHelp'),
            value:
              language === 'ro'
                ? feedback.parentHelpRo || feedback.parentHelp
                : feedback.parentHelp,
          },
        ]
      : []
  ).filter((section) => section.value || section.starRating);

  const isLoading = loadingInProgress || loadingCompleted || loadingUpcoming;

  return (
    <section className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-6 font-raleway text-white">
      <h2 className="text-lg font-bold">{t('parent_feedback_title')}</h2>

      {isLoading ? (
        <LoadingSpinner className="mx-auto" />
      ) : !courses.length ? (
        <p className="text-sm text-customGrey">{t('parent_feedback_no_courses')}</p>
      ) : (
        <>
          <Select
            className="max-w-md"
            variant="secondary"
            label={t('parent_feedback_course')}
            value={selectedId}
            allowClear={false}
            options={courses.map((course) => ({
              value: course.id,
              label: `${course.title}${
                course.duplicateIndex ? ` #${course.duplicateIndex}` : ''
              } — ${statusLabel(course.status)}`,
            }))}
            onSelect={(value) => setSelectedId(value as string)}
          />

          <div className="flex flex-col gap-4 rounded-xl bg-surfaceBackground p-4">
            {loadingSessions ? (
              <LoadingSpinner className="mx-auto" />
            ) : !currentSession ? (
              <p className="text-sm text-customGrey">{t('parent_feedback_none')}</p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    disabled={sessionIndex === 0}
                    onClick={() => setSessionIndex((index) => Math.max(0, index - 1))}
                    className="text-customGrey transition-colors hover:text-white disabled:opacity-30"
                    aria-label="Previous session"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="text-center text-sm font-semibold">{currentSession.title}</span>
                  <button
                    type="button"
                    disabled={sessionIndex >= feedbackSessions.length - 1}
                    onClick={() =>
                      setSessionIndex((index) => Math.min(feedbackSessions.length - 1, index + 1))
                    }
                    className="text-customGrey transition-colors hover:text-white disabled:opacity-30"
                    aria-label="Next session"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>

                {feedbackSections.length ? (
                  <div className="flex flex-col gap-4">
                    {feedbackSections.map((section) => (
                      <SessionDetailsFeedbackLabelValue key={section.label} {...section} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-customGrey">{t('parent_feedback_none')}</p>
                )}

                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    buttonText={t('parent_see_all_feedback')}
                    onClick={() =>
                      history.push(`/parent-view/${studentId}/sessions/${currentSession.sessionId}`)
                    }
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ParentPageFeedbackSection;
